import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Eye, Package, Printer, ShoppingCart, Search, X, Image, Video, Link as LinkIcon, Play } from 'lucide-react';
import { FileUploader } from './FileUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecipeDialog = ({ open, onOpenChange, recipe, onSave, t, availableIngredients }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language; // 'zh', 'en', or 'ru'
  
  const categories = ['早餐', '午餐', '晚餐', '小食', '甜点'];
  const difficulties = ['简单', '中等', '稍难'];
  const dietTypes = ['纯素', '蛋奶素'];

  // Helper to get field name based on language
  const getFieldName = (baseField) => {
    if (currentLang === 'en') return `${baseField}_en`;
    if (currentLang === 'ru') return `${baseField}_ru`;
    return baseField; // zh uses base field
  };

  const [formData, setFormData] = useState({
    // Base fields (Chinese)
    name: '', description: '', cook_time: '', ingredients: [], steps: [],
    // English fields
    name_en: '', description_en: '', cook_time_en: '', ingredients_en: [], steps_en: [],
    // Russian fields
    name_ru: '', description_ru: '', cook_time_ru: '', ingredients_ru: [], steps_ru: [],
    // Shared fields (not language-specific)
    image_url: '', video_url: '', difficulty: '简单', category: '早餐',
    diet_type: '纯素', is_active: true, is_daily_pick: false,
  });

  const [newStep, setNewStep] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showIngredientDropdown, setShowIngredientDropdown] = useState(false);
  const [customIngredientName, setCustomIngredientName] = useState('');
  const [customIngredientLink, setCustomIngredientLink] = useState('');
  const [editingIngredientIndex, setEditingIngredientIndex] = useState(null);

  // Normalize ingredient to object format
  const normalizeIngredient = (ing) => {
    if (typeof ing === 'string') {
      return { name: ing, link: null };
    }
    return { name: ing.name || '', link: ing.link || null };
  };

  // Get current language's ingredients/steps field
  const ingredientsField = getFieldName('ingredients');
  const stepsField = getFieldName('steps');
  const nameField = getFieldName('name');
  const descField = getFieldName('description');
  const cookTimeField = getFieldName('cook_time');

  useEffect(() => {
    if (recipe?.id) {
      setFormData({
        name: recipe.name || '', description: recipe.description || '', cook_time: recipe.cook_time || '',
        name_en: recipe.name_en || '', description_en: recipe.description_en || '', cook_time_en: recipe.cook_time_en || '',
        name_ru: recipe.name_ru || '', description_ru: recipe.description_ru || '', cook_time_ru: recipe.cook_time_ru || '',
        ingredients: (recipe.ingredients || []).map(normalizeIngredient),
        ingredients_en: (recipe.ingredients_en || []).map(normalizeIngredient),
        ingredients_ru: (recipe.ingredients_ru || []).map(normalizeIngredient),
        steps: recipe.steps || [],
        steps_en: recipe.steps_en || [],
        steps_ru: recipe.steps_ru || [],
        image_url: recipe.image_url || '', video_url: recipe.video_url || '',
        difficulty: recipe.difficulty || '简单', category: recipe.category || '早餐',
        diet_type: recipe.diet_type || '纯素', is_active: recipe.is_active !== false,
        is_daily_pick: recipe.is_daily_pick || false,
      });
    } else {
      setFormData({
        name: '', description: '', cook_time: '', ingredients: [], steps: [],
        name_en: '', description_en: '', cook_time_en: '', ingredients_en: [], steps_en: [],
        name_ru: '', description_ru: '', cook_time_ru: '', ingredients_ru: [], steps_ru: [],
        image_url: '', video_url: '', difficulty: '简单', category: '早餐',
        diet_type: '纯素', is_active: true, is_daily_pick: false,
      });
    }
    setNewStep('');
    setSearchTerm('');
    setCustomIngredientName('');
    setCustomIngredientLink('');
    setEditingIngredientIndex(null);
  }, [recipe]);

  // Get ingredients for the current language (considering available translations)
  const getIngredientDisplayName = (ing) => {
    // For ingredient selection, show the appropriate language version
    if (currentLang === 'en' && ing.name_en) return ing.name_en;
    if (currentLang === 'ru' && ing.name_ru) return ing.name_ru;
    return ing.name;
  };

  // Filter available ingredients based on search
  const filteredIngredients = availableIngredients?.filter(ing => {
    const searchLower = searchTerm.toLowerCase();
    return ing.name.toLowerCase().includes(searchLower) ||
      (ing.name_en && ing.name_en.toLowerCase().includes(searchLower)) ||
      (ing.name_ru && ing.name_ru.toLowerCase().includes(searchLower));
  }) || [];

  const addIngredientFromList = (ing) => {
    // Use the appropriate language name
    const ingredientName = getIngredientDisplayName(ing);
    const newIng = { name: ingredientName, link: ing.link || null };
    const currentIngredients = formData[ingredientsField] || [];
    if (!currentIngredients.some(i => i.name === newIng.name)) {
      setFormData({ ...formData, [ingredientsField]: [...currentIngredients, newIng] });
    }
    setSearchTerm('');
    setShowIngredientDropdown(false);
  };

  const addCustomIngredient = () => {
    if (customIngredientName.trim()) {
      const newIng = { name: customIngredientName.trim(), link: customIngredientLink.trim() || null };
      const currentIngredients = formData[ingredientsField] || [];
      if (!currentIngredients.some(i => i.name === newIng.name)) {
        setFormData({ ...formData, [ingredientsField]: [...currentIngredients, newIng] });
      }
      setCustomIngredientName('');
      setCustomIngredientLink('');
    }
  };

  const updateIngredientLink = (index, link) => {
    const currentIngredients = [...(formData[ingredientsField] || [])];
    currentIngredients[index] = { ...currentIngredients[index], link: link || null };
    setFormData({ ...formData, [ingredientsField]: currentIngredients });
    setEditingIngredientIndex(null);
  };

  const removeIngredient = (index) => {
    const currentIngredients = formData[ingredientsField] || [];
    setFormData({ ...formData, [ingredientsField]: currentIngredients.filter((_, i) => i !== index) });
    if (editingIngredientIndex === index) setEditingIngredientIndex(null);
  };

  const addStep = () => {
    if (newStep.trim()) {
      const currentSteps = formData[stepsField] || [];
      setFormData({ ...formData, [stepsField]: [...currentSteps, newStep.trim()] });
      setNewStep('');
    }
  };

  const removeStep = (index) => {
    const currentSteps = formData[stepsField] || [];
    setFormData({ ...formData, [stepsField]: currentSteps.filter((_, i) => i !== index) });
  };

  const moveStep = (index, direction) => {
    const currentSteps = [...(formData[stepsField] || [])];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < currentSteps.length) {
      [currentSteps[index], currentSteps[newIndex]] = [currentSteps[newIndex], currentSteps[index]];
      setFormData({ ...formData, [stepsField]: currentSteps });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Calculate ingredients_count from the main language's ingredients
    const ingredientsCount = Math.max(
      (formData.ingredients || []).length,
      (formData.ingredients_en || []).length,
      (formData.ingredients_ru || []).length
    );
    onSave({
      ...formData,
      ingredients_count: ingredientsCount,
      video_url: formData.video_url || null,
    });
  };

  // Current language values
  const currentName = formData[nameField] || '';
  const currentDesc = formData[descField] || '';
  const currentCookTime = formData[cookTimeField] || '';
  const currentIngredients = formData[ingredientsField] || [];
  const currentSteps = formData[stepsField] || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{recipe?.id ? t('admin.editRecipe') : t('admin.addRecipe')}</DialogTitle>
          <DialogDescription className="text-sm">
            {currentLang === 'zh' ? '🇨🇳 正在编辑中文版本' : currentLang === 'en' ? '🇬🇧 Editing English version' : '🇷🇺 Редактирование русской версии'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('admin.recipeName')}</Label>
              <Input value={currentName} onChange={(e) => setFormData({ ...formData, [nameField]: e.target.value })} required data-testid="recipe-name-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.category')}</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger data-testid="recipe-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (<SelectItem key={cat} value={cat}>{t(`admin.recipeCategories.${cat}`, cat)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('admin.recipeDesc')}</Label>
            <Textarea value={currentDesc} onChange={(e) => setFormData({ ...formData, [descField]: e.target.value })} required data-testid="recipe-description-input" />
          </div>
          <div className="space-y-2">
            <Label>{t('admin.recipeImage')}</Label>
            <div className="flex gap-2">
              <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder={t('admin.imageUrlPlaceholder')} className="flex-1" data-testid="recipe-image-url-input" />
              <FileUploader type="image" accept="image/jpeg,image/png,image/gif,image/webp" onUpload={(url) => setFormData({ ...formData, image_url: url })} label={t('admin.upload')} />
            </div>
            {formData.image_url && (<img src={formData.image_url} alt={t('admin.preview')} className="w-24 h-24 object-cover rounded mt-2" />)}
          </div>
          <div className="space-y-2">
            <Label>{t('admin.recipeVideo')}</Label>
            <div className="flex gap-2">
              <Input value={formData.video_url || ''} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })} placeholder={t('admin.videoUrlPlaceholder')} className="flex-1" />
              <FileUploader type="video" accept="video/mp4,video/webm,video/quicktime" onUpload={(url) => setFormData({ ...formData, video_url: url })} label={t('admin.upload')} />
            </div>
            {formData.video_url && (<video src={formData.video_url} controls className="w-full max-h-48 rounded mt-2" />)}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('admin.cookTime')}</Label>
              <Input value={currentCookTime} onChange={(e) => setFormData({ ...formData, [cookTimeField]: e.target.value })} placeholder={t('admin.cookTimePlaceholder')} required data-testid="recipe-cook-time-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('admin.difficulty')}</Label>
              <Select value={formData.difficulty} onValueChange={(v) => setFormData({ ...formData, difficulty: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {difficulties.map((d) => (<SelectItem key={d} value={d}>{t(`admin.difficulties.${d}`, d)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('admin.dietType')}</Label>
              <Select value={formData.diet_type} onValueChange={(v) => setFormData({ ...formData, diet_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {dietTypes.map((d) => (<SelectItem key={d} value={d}>{t(`admin.dietTypes.${d}`, d)}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Organic Ingredients Section with Selection */}
          <div className="space-y-3 border rounded-lg p-4 bg-green-50">
            <Label className="text-green-700 font-semibold">🌿 {t('admin.organicIngredients')}</Label>
            
            {/* Search and select from preset list */}
            <div className="relative">
              <Input 
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setShowIngredientDropdown(true); }}
                onFocus={() => setShowIngredientDropdown(true)}
                placeholder={t('admin.searchIngredient')}
                className="bg-white"
              />
              {showIngredientDropdown && searchTerm && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredIngredients.length > 0 ? (
                    filteredIngredients.slice(0, 10).map((ing) => (
                      <button
                        key={ing.id}
                        type="button"
                        onClick={() => addIngredientFromList(ing)}
                        className="w-full px-3 py-2 text-left hover:bg-green-50 flex justify-between items-center"
                      >
                        <span>{ing.name}</span>
                        {ing.link && <span className="text-xs text-blue-500">🔗</span>}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-gray-500 text-sm">{t('admin.noIngredientFound')}</div>
                  )}
                </div>
              )}
            </div>

            {/* Custom ingredient input */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs text-gray-600">{t('admin.customIngredient')}</Label>
                <Input
                  value={customIngredientName}
                  onChange={(e) => setCustomIngredientName(e.target.value)}
                  placeholder={t('admin.ingredientPlaceholder')}
                  className="bg-white"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-gray-600">{t('admin.ingredientLink')}</Label>
                <Input
                  value={customIngredientLink}
                  onChange={(e) => setCustomIngredientLink(e.target.value)}
                  placeholder={t('admin.ingredientLinkPlaceholder')}
                  className="bg-white"
                />
              </div>
              <Button type="button" onClick={addCustomIngredient} className="bg-green-600 hover:bg-green-700">{t('admin.addIngredient')}</Button>
            </div>

            {/* Selected ingredients list */}
            {currentIngredients.length > 0 && (
              <div className="space-y-2 mt-3">
                {currentIngredients.map((ingredient, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border border-green-200">
                    <span className="flex-1 text-green-700 font-medium">{ingredient.name}</span>
                    {editingIngredientIndex === index ? (
                      <div className="flex gap-1 items-center">
                        <Input
                          value={ingredient.link || ''}
                          onChange={(e) => {
                            const newIngs = [...currentIngredients];
                            newIngs[index] = { ...newIngs[index], link: e.target.value };
                            setFormData({ ...formData, [ingredientsField]: newIngs });
                          }}
                          placeholder="URL"
                          className="w-40 h-8 text-xs"
                        />
                        <Button type="button" size="sm" variant="ghost" onClick={() => setEditingIngredientIndex(null)} className="h-8 px-2">✓</Button>
                      </div>
                    ) : (
                      <>
                        {ingredient.link ? (
                          <a href={ingredient.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 text-xs hover:underline">🔗 {t('admin.ingredientLink')}</a>
                        ) : (
                          <span className="text-gray-400 text-xs">{t('admin.ingredientLinkPlaceholder')}</span>
                        )}
                        <Button type="button" size="sm" variant="ghost" onClick={() => setEditingIngredientIndex(index)} className="h-8 px-2 text-gray-500">✏️</Button>
                      </>
                    )}
                    <button type="button" onClick={() => removeIngredient(index)} className="text-red-500 hover:text-red-700 px-1">×</button>
                  </div>
                ))}
              </div>
            )}
            {currentIngredients.length === 0 && (
              <p className="text-sm text-gray-500">{t('admin.noIngredientsYet')}</p>
            )}
          </div>

          {/* Cooking Steps Section */}
          <div className="space-y-2 border rounded-lg p-4 bg-orange-50">
            <Label className="text-orange-700 font-semibold">📝 {t('admin.cookingSteps')}</Label>
            <div className="flex gap-2">
              <Textarea 
                value={newStep} 
                onChange={(e) => setNewStep(e.target.value)} 
                placeholder={t('admin.stepPlaceholder')}
                className="min-h-[60px]"
              />
              <Button type="button" onClick={addStep} className="bg-orange-500 hover:bg-orange-600 self-end">{t('admin.addStep')}</Button>
            </div>
            {currentSteps.length > 0 && (
              <div className="space-y-2 mt-2">
                {currentSteps.map((step, index) => (
                  <div key={index} className="flex items-start gap-2 bg-white p-3 rounded border">
                    <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <p className="flex-1 text-sm text-gray-700">{step}</p>
                    <div className="flex flex-col gap-1">
                      <button type="button" onClick={() => moveStep(index, -1)} disabled={index === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">↑</button>
                      <button type="button" onClick={() => moveStep(index, 1)} disabled={index === currentSteps.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">↓</button>
                    </div>
                    <button type="button" onClick={() => removeStep(index)} className="text-red-500 hover:text-red-700">×</button>
                  </div>
                ))}
              </div>
            )}
            {currentSteps.length === 0 && (
              <p className="text-sm text-gray-500">{t('admin.noStepsYet')}</p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="recipe_is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} className="rounded" />
              <Label htmlFor="recipe_is_active">{t('admin.show')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="recipe_is_daily_pick" 
                checked={formData.is_daily_pick} 
                onChange={(e) => setFormData({ ...formData, is_daily_pick: e.target.checked })} 
                className="rounded border-orange-300 text-orange-500 focus:ring-orange-500" 
              />
              <Label htmlFor="recipe_is_daily_pick" className="text-orange-600 font-medium">⭐ {t('admin.dailyPick', '每日推荐')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('admin.cancel')}</Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="save-recipe-button">{t('admin.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Admin Recipes Component
const AdminRecipes = ({ recipes, onRefresh }) => {
  const [editRecipe, setEditRecipe] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const { token } = useAuth();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  // Helper to get localized field
  const getLocalizedValue = (item, field) => {
    if (currentLang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    if (currentLang === 'ru' && item[`${field}_ru`]) return item[`${field}_ru`];
    return item[field] || '';
  };

  // Load available ingredients
  useEffect(() => {
    const loadIngredients = async () => {
      try {
        const res = await axios.get(`${API}/ingredients`);
        setAvailableIngredients(res.data);
      } catch (err) {
        console.error('Failed to load ingredients:', err);
      }
    };
    loadIngredients();
  }, []);

  const handleSave = async (recipeData) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editRecipe?.id) {
        await axios.put(`${API}/recipes/${editRecipe.id}`, recipeData, { headers });
        toast.success(t('admin.recipeUpdateSuccess'));
      } else {
        await axios.post(`${API}/recipes`, recipeData, { headers });
        toast.success(t('admin.recipeCreateSuccess'));
      }
      setIsDialogOpen(false);
      setEditRecipe(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.operationFailed'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.confirmDeleteRecipe'))) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/recipes/${id}`, { headers });
      toast.success(t('admin.recipeDeleteSuccess'));
      onRefresh();
    } catch (err) {
      toast.error(t('admin.deleteFailed'));
    }
  };

  return (
    <div data-testid="admin-recipes">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">{t('admin.recipes')}</h1>
        <Button className="bg-green-600 hover:bg-green-700" onClick={() => { setEditRecipe({}); setIsDialogOpen(true); }} data-testid="add-recipe-button">
          <Plus className="w-4 h-4 mr-2" /> {t('admin.addRecipe')}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.image')}</TableHead>
                <TableHead>{t('admin.name')}</TableHead>
                <TableHead>{t('admin.category')}</TableHead>
                <TableHead>{t('admin.difficulty')}</TableHead>
                <TableHead>{t('admin.dietType')}</TableHead>
                <TableHead>{t('admin.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipes.map((recipe) => (
                <TableRow key={recipe.id}>
                  <TableCell><img src={recipe.image_url} alt={getLocalizedValue(recipe, 'name')} className="w-12 h-12 object-cover rounded" /></TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {getLocalizedValue(recipe, 'name')}
                      {recipe.is_daily_pick && <Badge className="bg-orange-500 text-white text-xs">⭐ {t('admin.dailyPick', '每日推荐')}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>{t(`admin.recipeCategories.${recipe.category}`, recipe.category)}</TableCell>
                  <TableCell><Badge variant="outline">{t(`admin.difficulties.${recipe.difficulty}`, recipe.difficulty)}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{t(`admin.dietTypes.${recipe.diet_type}`, recipe.diet_type)}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="icon" variant="ghost" onClick={() => { setEditRecipe(recipe); setIsDialogOpen(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(recipe.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <RecipeDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} recipe={editRecipe} onSave={handleSave} t={t} availableIngredients={availableIngredients} />
    </div>
  );
};


export default AdminRecipes;
